import { ChatAvatar, GroupIcon } from '@halo/ui-chat-react';
import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import type { Session } from '@/store/client';
import { AccountType, StateEnum } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import { isSessionCategory, SessionCategoryPrefix } from '@/store/client';
import { ChatMetaContentWithData } from '@/pages/main/messages/ChatList/session-item-with-data';
import useChatActive from './useChatActive';

export interface SessionItemProps {
  session: Session;
  shouldAnimate: boolean;
  hiddenBorder: boolean;
}

export const SessionItem = memo(function SessionItem({
  session,
  hiddenBorder,
}: SessionItemProps) {
  const { chat_id, name, last_message, user_info } = session;
  const { fetchProfileUser, setGlobalSearchIsOpen } = useActionsStore(
    useShallowEnhance(['fetchProfileUser', 'setGlobalSearchIsOpen'])
  );
  const chatActive = useChatActive(chat_id);
  const isGroup = useMemo(
    () => isSessionCategory(chat_id, SessionCategoryPrefix.Group),
    [chat_id]
  );
  const isRobot = useMemo(
    () => session?.user_info?.account_type === AccountType.Robot,
    [session?.user_info?.account_type]
  );

  const handleAvatarClick = useCallback(async () => {
    if (!isGroup && user_info?.user_id) {
      fetchProfileUser(user_info.user_id);
    }
  }, [fetchProfileUser, isGroup, user_info?.user_id]);

  return (
    <Flex
      display="flex"
      alignItems="center"
      p="2"
      pb={0}
      gap="3"
      cursor="pointer"
      _hover={{
        bg: 'border.subtle',
      }}
      onClick={() => {
        chatActive();
        setGlobalSearchIsOpen(false);
      }}
    >
      <ChatAvatar
        src={session.avatar}
        duration={session.expire_duration}
        showExpireIcon={false}
        onClick={handleAvatarClick}
        activeShake={!isGroup}
      />
      <Box
        className="ChatMeta"
        flex="1"
        overflow="hidden"
        borderBottom={hiddenBorder ? 'none' : '1px solid'}
        borderColor="border.subtle"
        pb={2}
        gap={1}
      >
        <HStack
          className="ChatMetaHeader"
          justify="space-between"
          align="center"
          gap="1"
          fontSize="sm"
        >
          {isGroup && (
            <Icon>
              <GroupIcon />
            </Icon>
          )}
          <Box flex="1" overflow="hidden" lineHeight="1">
            <Text fontSize="1em" fontWeight="semibold" truncate>
              {isGroup
                ? name
                : user_info?.state === StateEnum.DELETE_ACCOUNT
                  ? name
                  : user_info?.name}
            </Text>
          </Box>
        </HStack>

        <ChatMetaContentWithData
          last_message={last_message}
          isGroup={isGroup}
          session={session}
          isRobot={isRobot}
        />
      </Box>
    </Flex>
  );
});
